import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { getParentClients, getChildClients } from '../utilities/AppCaches';

const ClientsContext = createContext();

/**
 * Hook to access cached client data across components
 */
export const useClients = () => {
  const context = useContext(ClientsContext);
  if (!context) {
    throw new Error('useClients must be used within a ClientsProvider');
  }
  return context;
};

/**
 * Provider component to cache and share client data across multiple components
 */
export const ClientsProvider = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();

  // Cache states
  const [parentClientsCache, setParentClientsCache] = useState(null);
  const [childClientsCache, setChildClientsCache] = useState({});
  const [loadingStates, setLoadingStates] = useState({
    parentClients: false,
    childClients: {},
  });

  // Track ongoing requests to prevent duplicates
  const requestsRef = useRef({
    parentClients: null,
    childClients: {},
  });

  /**
   * Fetch parent clients with caching
   */
  const fetchParentClients = useCallback(
    async (forceRefresh = false) => {
      // Return cached data if available and not forcing refresh
      if (parentClientsCache && !forceRefresh) {
        return parentClientsCache;
      }

      // Return existing request if already in progress
      if (requestsRef.current.parentClients) {
        try {
          const result = await requestsRef.current.parentClients;
          return Array.isArray(result) ? result : [];
        } catch (error) {
          return [];
        }
      }

      setLoadingStates((prev) => ({ ...prev, parentClients: true }));

      try {
        // Use cached parent clients from AppCaches
        const requestPromise = getParentClients();

        requestsRef.current.parentClients = requestPromise;

        // Get the cached response
        const clientsArray = await requestPromise;

        setParentClientsCache(clientsArray);
        return clientsArray;
      } catch (error) {
        enqueueSnackbar('An error occurred while fetching parent clients.', { variant: 'error' });
        return [];
      } finally {
        setLoadingStates((prev) => ({ ...prev, parentClients: false }));
        requestsRef.current.parentClients = null;
      }
    },
    [parentClientsCache, enqueueSnackbar],
  );

  /**
   * Fetch child clients with caching (supports different filter configurations)
   */
  const fetchChildClients = useCallback(
    async (filter = {}, forceRefresh = false) => {
      const filterKey = JSON.stringify(filter);

      // Return cached data if available and not forcing refresh
      if (childClientsCache[filterKey] && !forceRefresh) {
        return childClientsCache[filterKey];
      }

      // Return existing request if already in progress
      if (requestsRef.current.childClients[filterKey]) {
        try {
          const result = await requestsRef.current.childClients[filterKey];
          return Array.isArray(result) ? result : [];
        } catch (error) {
          return [];
        }
      }

      setLoadingStates((prev) => ({
        ...prev,
        childClients: { ...prev.childClients, [filterKey]: true },
      }));

      try {
        // Use cached child clients from AppCaches
        const requestPromise = getChildClients(filter);

        requestsRef.current.childClients[filterKey] = requestPromise;

        // Get the cached response
        const clientsArray = await requestPromise;

        setChildClientsCache((prev) => ({ ...prev, [filterKey]: clientsArray }));
        return clientsArray;
      } catch (error) {
        enqueueSnackbar('An error occurred while fetching child clients.', { variant: 'error' });
        return [];
      } finally {
        setLoadingStates((prev) => ({
          ...prev,
          childClients: { ...prev.childClients, [filterKey]: false },
        }));
        if (requestsRef.current.childClients[filterKey]) {
          delete requestsRef.current.childClients[filterKey];
        }
      }
    },
    [childClientsCache, enqueueSnackbar],
  );

  /**
   * Get clients with optional "All Clients" item
   */
  const getClientsWithAllOption = useCallback((clients, showAllClients = false) => {
    if (!showAllClients) {
      return clients;
    }

    const allClients = {
      name: 'All Clients',
      id: 0,
    };

    return [allClients, ...clients];
  }, []);

  /**
   * Clear cache (useful for when data needs to be refreshed)
   */
  const clearCache = useCallback(() => {
    setParentClientsCache(null);
    setChildClientsCache({});
    requestsRef.current = { parentClients: null, childClients: {} };
  }, []);

  /**
   * Get loading state for specific request
   */
  const isLoading = useCallback(
    (type, filter = null) => {
      if (type === 'parentClients') {
        return loadingStates.parentClients;
      } else if (type === 'childClients') {
        const filterKey = filter ? JSON.stringify(filter) : '{}';
        return loadingStates.childClients[filterKey] || false;
      }
      return false;
    },
    [loadingStates],
  );

  const contextValue = {
    // Data
    parentClientsCache,
    childClientsCache,

    // Methods
    fetchParentClients,
    fetchChildClients,
    getClientsWithAllOption,
    clearCache,
    isLoading,
  };

  return <ClientsContext.Provider value={contextValue}>{children}</ClientsContext.Provider>;
};

export default ClientsContext;
